/**
 * @file ReceiptMethodFields.tsx
 * @description Выбор способа получения и поля исхода — общий блок формы
 * создания заказа и блока «Согласование доставки» (phase19.24 шаг 5.1, §2, §6).
 *
 * Один компонент на два экрана намеренно: менеджер выставляет условия либо
 * при создании, либо в карточке, и две копии карточек-исходов разъехались бы
 * на первой же правке текста. Что показывать для исхода, решает реестр
 * `lib/receipt-method.ts`, а не `if` здесь.
 *
 * Радио-карточки — настоящие `input[type=radio]`: клавиатура и скринридер
 * получают группу переключателей даром, а тач-цель поднята до 56px на `<lg`
 * (правило mobile-first: 360px проверяем первым).
 */

import type { ReactNode } from "react";

import {
  isSelectableReceiptMethod,
  RECEIPT_METHOD_META,
  RECEIPT_METHOD_ORDER,
} from "../../lib/receipt-method";
import type { OrderReceiptMethod } from "../../lib/api-orders";
import type { ReceiptFormValue, ReceiptIssue } from "../../lib/receipt-method";
import type { ShopPickupLocationDto } from "../../lib/api-shop-pickup";
import { PickupLocationCard } from "./PickupLocationCard";
import { ReceiptOutcomeFields } from "./ReceiptOutcomeFields";
import { NoticeStrip } from "../ui/NoticeStrip";
import { SectionLabel } from "../ui/SectionLabel";
import { cn } from "@/lib/utils";

/** Состояние справочника пункта самовывоза — грузится один раз на странице. */
export interface PickupLocationState {
  location: ShopPickupLocationDto | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

interface Props {
  value: ReceiptFormValue;
  onChange: (next: ReceiptFormValue) => void;
  pickup: PickupLocationState;
  /** Проблема формы от `validateReceiptForm` — подпись под нужным полем. */
  issue: ReceiptIssue | null;
  disabled?: boolean;
  shopSlug?: string;
  /** Идентификатор формы: на странице бывает и создание, и согласование. */
  idPrefix?: string;
  /**
   * Блок расчёта перевозчика между адресом и стоимостью (phase19.24 шаг 5.2).
   *
   * Слот, а не ветка внутри: калькулятор есть только в карточке заказа (там
   * известны позиции и места), а при создании заказа сумму ставят руками.
   * Порядок полей при этом один и тот же — «куда → чем посчитали → сколько».
   */
  quoteSlot?: ReactNode;
  /** Подпись под полем стоимости, если её подставил расчёт. */
  amountHint?: string;
}

export function ReceiptMethodFields({
  value,
  onChange,
  pickup,
  issue,
  disabled,
  shopSlug,
  idPrefix = "receipt",
  quoteSlot,
  amountHint,
}: Props) {
  const patch = (partial: Partial<ReceiptFormValue>): void =>
    onChange({ ...value, ...partial });
  // Способ из истории (`AGREEMENT` витрины и старых заказов): карточки выбора
  // у него нет — показываем строкой и ждём, что менеджер выберет исход.
  const legacyMethod = !isSelectableReceiptMethod(value.method);

  return (
    <div className="flex flex-col gap-4">
      <SectionLabel as="h2">Способ получения</SectionLabel>
      <p className="-mt-2 text-sm text-on-surface-variant">
        Как клиент получит заказ. Способ и итоговую сумму клиент подтверждает
        письмом.
      </p>

      <div
        role="radiogroup"
        aria-label="Способ получения"
        className="grid grid-cols-1 gap-2 lg:grid-cols-2"
      >
        {RECEIPT_METHOD_ORDER.map((method) => (
          <MethodCard
            key={method}
            method={method}
            name={`${idPrefix}-method`}
            checked={value.method === method}
            disabled={disabled}
            onSelect={() => patch({ method })}
          />
        ))}
      </div>

      {legacyMethod ? (
        <NoticeStrip tone="warning">
          {issue?.field === "method"
            ? issue.message
            : `${RECEIPT_METHOD_META[value.method].title}: выберите способ получения — клиенту нужно знать, как он получит заказ.`}
        </NoticeStrip>
      ) : value.method === "PICKUP" ? (
        <PickupLocationCard
          location={pickup.location}
          loading={pickup.loading}
          error={pickup.error}
          onRetry={pickup.reload}
          shopSlug={shopSlug}
          // Причина запрета отправки стоит у самого блока пункта, а не только
          // общей плашкой внизу формы (находка ревью 5.1).
          issue={issue?.field === "pickup" ? issue.message : null}
        />
      ) : (
        <ReceiptOutcomeFields
          value={value}
          onChange={onChange}
          issue={issue}
          disabled={disabled}
          idPrefix={idPrefix}
          quoteSlot={quoteSlot}
          amountHint={amountHint}
        />
      )}

      {value.method === "PICKUP" ? (
        <NoticeStrip>
          Позиция-услуга «Доставка» не создаётся: самовывоз бесплатный.
        </NoticeStrip>
      ) : null}
    </div>
  );
}

/** Радио-карточка исхода: 56px на `<lg`, 48px на десктопе. */
function MethodCard({
  method,
  name,
  checked,
  disabled,
  onSelect,
}: {
  method: OrderReceiptMethod;
  name: string;
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  const meta = RECEIPT_METHOD_META[method];
  return (
    <label
      className={cn(
        "flex min-h-14 cursor-pointer items-start gap-3 rounded-xl px-4 py-3 transition-colors lg:min-h-12",
        checked ? "bg-primary/10" : "bg-surface-low hover:bg-surface-low/70",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <input
        type="radio"
        name={name}
        className="mt-1 size-4 accent-[var(--primary)]"
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
      />
      <span className="min-w-0">
        <span className="block text-base font-medium text-on-surface">{meta.title}</span>
        <span className="block text-sm text-on-surface-variant">{meta.hint}</span>
      </span>
    </label>
  );
}
